// Approved Jambushrushti content. Text mirrors the live site; images are from the
// Siddhi Jambu Parivar archive (siddhijambuparivar.com), stored locally under /public/images.

export const SITE = {
  name: "Jambushrushti",
  tagline: "Jain Knowledge Ecosystem",
  email: "contact@jambushrushti.org",
  phone: "+91-98250 44911",
  officeHours: "Mon – Sat: 10:00 AM – 6:00 PM",
  visitingHours: "Mon – Sat: 10:45 AM – 5:15 PM",
  address: [
    "Muni Jambuvijayaji Gyan Mandir,",
    "Near Adani Shantigram Jain Temple,",
    "Adani Shantigram Township, Ahmedabad 382 421",
  ],
  mapUrl: "https://maps.app.goo.gl/mk3KCxWMSVLmXYiC6",
  adaniUrl: "https://www.adanifoundation.org/",
  parivarUrl: "https://siddhijambuparivar.com/",
};

// Official social channels. Only the YouTube channel linked from siddhijambuparivar.com is known;
// add Facebook / Instagram / X links here when available and they appear in the footer.
export type Social = {
  network: "youtube" | "facebook" | "instagram" | "x";
  label: string;
  href: string;
};

export const SOCIAL: Social[] = [
  {
    network: "youtube",
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCnFisBimrK2aIE-hnY70kCA",
  },
];

export type NavItem = {
  label: string;
  href: string;
  description?: string;
  badge?: string;
};
export type NavGroup = { label: string; href?: string; children?: NavItem[] };

export const NAV: NavGroup[] = [
  {
    label: "About",
    children: [
      {
        label: "About Jambushrushti",
        href: "/about",
        description: "Vision & mission of our platform",
      },
      {
        label: "About Gurudev",
        href: "/about/gurudev",
        description: "Life, teachings and legacy",
      },
      {
        label: "Parivar & Legacy",
        href: "/about/parivar",
        description: "Lineage & disciples",
      },
      {
        label: "Muni Jambuvijayaji Research Center",
        href: "/about/gyan-kendra",
        description: "Digital museum of teachings",
      },
      {
        label: "Adani Shantigram History",
        href: "/about/adani-shantigram",
        description: "The story of Adani Shantigram",
        badge: "New",
      },
    ],
  },
  { label: "Guruvani", href: "/guruvani" },
  {
    label: "Library",
    children: [
      {
        label: "Library Catalog",
        href: "/library",
        description: "Browse the full collection",
      },
      {
        label: "Books & Manuscripts",
        href: "/library/books",
        description: "Publications and rare manuscripts",
      },
      {
        label: "eMagazine – Jambu Jyoti",
        href: "/emagazine",
        description: "Digital magazine archive",
      },
    ],
  },
  {
    label: "Gallery",
    children: [
      {
        label: "Photo & Video Gallery",
        href: "/gallery",
        description: "Rare photographs and videos from Gurudev's life",
      },
      {
        label: "Live Telecast",
        href: "/live-telecast",
        description: "Live events & broadcasts",
      },
    ],
  },
  {
    label: "Community",
    children: [
      {
        label: "Events",
        href: "/community/events",
        description: "Workshops & lectures",
      },
      {
        label: "Blogs & Articles",
        href: "/community/blog",
        description: "Insights & articles",
      },
    ],
  },
];

/*
 * The research tools (SodhSanchay, SodhSandarbh, Śabdasaṅgraha, Shastrasandarbha) and the
 * /research page are hidden until the client approves them. Set to true to bring them back:
 * the routes, tool lists, links and cards all follow this flag.
 */
export const SHOW_RESEARCH_TOOLS = false;

/** Where "Research" links go: the toolkit when shown, otherwise the Research Centre page. */
export const RESEARCH_HREF = SHOW_RESEARCH_TOOLS
  ? "/research"
  : "/about/gyan-kendra";

export const QUICK_LINKS: NavItem[] = [
  { label: "About Us", href: "/about" },
  ...(SHOW_RESEARCH_TOOLS ? [{ label: "Research", href: "/research" }] : []),
  { label: "Library", href: "/library" },
  { label: "Guruvani", href: "/guruvani" },
  { label: "Contact Us", href: "/contact" },
];

export type ResearchTool = {
  title: string;
  subtitle: string;
  href: string;
  icon: "layers" | "search" | "pdf" | "network";
};

export const RESEARCH_TOOLS: ResearchTool[] = [
  {
    title: "SodhSanchay",
    subtitle: "Research Metadata Entry",
    href: "/research/sodhsanchay",
    icon: "layers",
  },
  {
    title: "SodhSandarbh",
    subtitle: "Search & Export Research",
    href: "/research/sodhsandarbh",
    icon: "search",
  },
  {
    title: "Śabdasaṅgraha",
    subtitle: "Smart PDF Search Engine",
    href: "/research/shabdasangraha",
    icon: "pdf",
  },
  {
    title: "Shastrasandarbha",
    subtitle: "AI Global Book Finder",
    href: "/research/shastrasandarbha",
    icon: "network",
  },
];

export const HERO_IMAGE = {
  src: "/images/gurudev/seated.webp",
  alt: "Gurudev Muni Jambuvijayaji Maharaj Saheb seated in white robes",
};

export const ABOUT_IMAGES = {
  main: {
    src: "/images/gurudev/portrait.webp",
    alt: "Portrait of Gurudev Muni Jambuvijayaji Maharaj Saheb",
  },
  top: {
    src: "/images/gurudev/research-session.webp",
    alt: "Gurudev with fellow monks during a research session",
  },
  bottom: {
    src: "/images/gurudev/manuscript-study.webp",
    alt: "Gurudev studying manuscripts with a sadhviji",
  },
};

export type Photo = { src: string; alt: string; position?: string };

export const GALLERY_PHOTOS: Photo[] = [
  {
    src: "/images/parivar/portrait-elder.webp",
    alt: "Gurudev Muni Jambuvijayaji Maharaj Saheb, portrait",
    position: "50% 25%",
  },
  {
    src: "/images/gallery/manuscript-study.webp",
    alt: "Gurudev reviewing manuscripts with disciples",
    position: "60% 50%",
  },
  {
    src: "/images/gallery/tirth-yatra.webp",
    alt: "Gurudev on a tirth yatra below a hilltop temple",
    position: "50% 30%",
  },
  {
    src: "/images/gallery/pravachan.webp",
    alt: "Gurudev delivering a pravachan",
    position: "50% 45%",
  },
];

export type Video = {
  id: string;
  title: string;
  thumb: string;
  position?: string;
};

// Published on YouTube and featured on siddhijambuparivar.com. Thumbnails are frames from
// each video with the letterboxing trimmed off.
export const VIDEOS: Video[] = [
  {
    id: "dzsjq-HG57I",
    title: "Shatabdi Mahotsav",
    thumb: "/images/videos/shatabdi-mahotsav.webp",
    position: "50% 28%",
  },
  {
    id: "uw4EDHxAf7s",
    title: "Tribute to Shree Jambu Vijayji",
    thumb: "/images/videos/tribute.webp",
    position: "50% 30%",
  },
  {
    id: "eANDEpHmPJc",
    title: "Jambu Vijayji Maharaj Saheb ni 12mi Tithi",
    thumb: "/images/videos/barmi-tithi.webp",
    position: "50% 30%",
  },
  {
    id: "GQVx45r5Wag",
    title: "Jambu Vijayji Janm Shatabdi Mahotsav",
    thumb: "/images/videos/janm-shatabdi-mahotsav.webp",
    position: "50% 20%",
  },
];

export type Book = { title: string; cover: string; href: string };

// Publications listed at siddhijambuparivar.com/shop.
export const BOOKS: Book[] = [
  {
    title: "Guruvani 1",
    cover: "/images/books/guruvani-1.webp",
    href: "https://siddhijambuparivar.com/product/book13/",
  },
  {
    title: "Gurubhakti Gahuli Sangrah",
    cover: "/images/books/gurubhakti-gahuli-sangrah.webp",
    href: "https://siddhijambuparivar.com/product/book12/",
  },
  {
    title: "Guruvani 2",
    cover: "/images/books/guruvani-2.webp",
    href: "https://siddhijambuparivar.com/product/book14/",
  },
  {
    title: "Guruvani 3",
    cover: "/images/books/guruvani-3.webp",
    href: "https://siddhijambuparivar.com/product/book17/",
  },
];
